/**
 * MONSTER TOWN — PLACEABLE BATCHER. OWNED BY PERF (src/perf/**).
 *
 * THIS IS THE BALLGAME. A kid fills the plot; the maxed-town fixture places ~900
 * objects and, unbatched, the town alone submitted 2,944 meshes and the frame
 * cost 3,410 draw calls. An older iPad does not survive that. Every prop in this
 * game is built from six to thirty chunky boxes plus an inverted-hull ink shell
 * on each one, and every single box was its own draw call.
 *
 * WHAT IT DOES. For each awake scene, every placed instance's STATIC meshes are
 * merged — per (material, cast-shadow, receive-shadow) bucket — into one
 * geometry, and all instances sharing a bucket are drawn from ONE
 * `THREE.BatchedMesh`. A whole town of paths, trees, fences, benches and flowers
 * collapses to roughly one draw call per material.
 *
 * WHY PICKING STILL WORKS. The original meshes are NOT removed, only hidden
 * (`visible = false`). `THREE.Raycaster` does not test visibility — it walks
 * whatever tree you hand it — so `pickInstance` keeps hitting each instance's
 * real geometry and walking up to the tagged art group exactly as before. No
 * proxy boxes, no second hit-test path that could drift out of sync with the
 * art. The batch meshes themselves have `raycast` stubbed out so they can never
 * shadow a real hit.
 *
 * WHY ANIMATION STILL WORKS (LAW #1 — everything you tap does something).
 *  - A prop that animates a NAMED part (`lid`, `bulb`, `bulbOff`) is safe by
 *    construction: named parts, and anything under a named ancestor, are never
 *    baked. They stay live meshes.
 *  - A prop that animates its ROOT (the `wobble` fallback, which is most of
 *    them) cannot be transformed inside a merged buffer, so the moment an
 *    instance animates it is UNBATCHED — one `setVisibleAt(false)` per bucket
 *    entry and its own meshes come back. That is O(entries), no geometry
 *    rebuild, no hitch. It rebakes when the clip ends.
 *
 * WHAT IS NEVER BATCHED, deliberately:
 *  - transparent materials (merging them destroys per-object depth sorting),
 *  - named parts and their subtrees (see above),
 *  - anything in `actors` — monsters are skinned-by-hand rigs that move every
 *    frame,
 *  - a scene that is not awake — those are already detached from the graph.
 *
 * FAILURE IS ALWAYS SAFE. If a bucket is full, or a geometry will not merge, the
 * instance simply stays unbatched: more draw calls, identical picture. There is
 * no state in which a batching failure loses a kid's prop.
 */

import * as THREE from 'three'
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js'
import { bus } from '../core/events'
import { engine } from '../core/engine'
import { world, type SceneNode } from '../core/scene'
import type { InstanceId, SceneId } from '../core/types'
import { bakeableMesh, harvestTargets } from './bakeable'

/**
 * Instance capacity for a bucket created ONE PLACEMENT AT A TIME (a kid
 * building). A full scene bake sizes its buckets from the measured need instead.
 *
 * This used to be a flat 1024 for every bucket, which measurably made small
 * scenes SLOWER: `BatchedMesh` carries a per-instance matrix texture and
 * per-frame bookkeeping sized by capacity, so thirteen near-empty 1024-slot
 * batches cost more every frame than the fifty-four draw calls they saved. The
 * demo-layout gate went from 12.7 ms to 16.6 ms on the older-iPad profile — a
 * regression introduced by the optimisation, caught by the gate that already
 * existed.
 */
const BUCKET_INSTANCES = 96

/**
 * Below this many placements a scene is not even MEASURED.
 *
 * Deliberately low and only a pre-filter: the real decision is the mesh count
 * below, and this exists so a scene holding three blocks is not traversed twice
 * a second to be told so again.
 */
const BATCH_MIN_PLACEMENTS = 8

/** Below this many bakeable meshes there is not enough on the table to bother. */
const BATCH_MIN_MESHES = 96

/**
 * MESHES PER BUCKET below which batching is a LOSS. This is the real threshold,
 * and it is a ratio because batching pays for REPETITION, not for volume.
 *
 * A bucket is one `THREE.BatchedMesh`: a per-instance matrix texture, a bounding
 * sphere and multi-draw bookkeeping, all paid every frame whether the bucket
 * holds two meshes or two hundred. So the trade is `meshes - buckets` draws
 * saved against `buckets` fixed costs, and it only comes out ahead when many
 * meshes collapse into each bucket — which happens when a kid has placed the
 * SAME THING many times, not when they have placed many different things.
 *
 * MEASURED, on the shipped bundle, `ipad-old`, idle box, three runs each. The
 * demo town holds 16 placements of 16 different defs: 171 bakeable meshes over
 * **37 buckets — 4.6 meshes each**. Batching it removed 134 draw calls and made
 * the frame NINE MILLISECONDS SLOWER:
 *
 *   | town, 21:30      | draws | work p50           |
 *   | unbatched        |  499  | 14.5 ms            |
 *   | batched          |  361  | 26.7 / 26.8 / 26.9 |
 *
 * That prices a bucket at roughly eighteen draw calls, and it is the same
 * failure the flat-1024-capacity version of this file hit — the one the header
 * above records — arrived at from the other direction. A gate on the mesh count
 * alone walks straight into it, because 171 meshes is plenty of meshes; they
 * were just spread across 37 materials with nothing to merge.
 *
 * 20 is set one notch above the measured break-even so the decision is never
 * taken on the wrong side of noise. The maxed-town fixture and a furnished
 * interior — a shop whose seventeen plinths are all the SAME def, and therefore
 * share materials — clear it comfortably.
 *
 * (The previous gate, 48 PLACEMENTS, got the right answer for both of these by
 * accident: a kid who has placed 48 things has almost certainly placed
 * duplicates. It was a proxy for repetition. This measures it.)
 */
const BATCH_MIN_MESHES_PER_BUCKET = 20
/** Headroom over the measured need when a full scene bake sizes its buffers. */
const HEADROOM = 1.35
/** Smallest buffer worth allocating — a bucket created by a single placement. */
const MIN_VERTS = 2048
/** Unbatched instances tolerated before the scene is rebaked from scratch. */
const REBAKE_AFTER = 24
/**
 * Never full-rebake a scene more often than this.
 *
 * Without it, one kid placing five blocks in a town that already had 25
 * unbatchable props triggered five full 500-instance rebakes — the condition is
 * a standing property of the scene, not an event, so every placement re-fired
 * it. A full bake is the most expensive thing in this file and must be rare.
 */
const REBAKE_COOLDOWN_MS = 8000

interface Bucket {
  key: string
  material: THREE.Material
  cast: boolean
  receive: boolean
  renderOrder: number
  mesh: THREE.BatchedMesh | null
  full: boolean
}

interface Entry {
  bucket: Bucket
  geometryId: number
  batchId: number
}

interface Baked {
  entries: Entry[]
  /** Meshes hidden by the bake, restored verbatim on unbake. */
  hidden: THREE.Mesh[]
  /**
   * Sub-groups whose ENTIRE subtree was baked, hidden as one node.
   *
   * Hiding 2,900 leaf meshes still leaves `WebGLRenderer.projectObject` and
   * `updateMatrixWorld` walking all 2,900 of them every frame — measured, that
   * traversal was the whole remaining cost once the draw calls were gone
   * (`render.frame` at 39 ms with only 624 draws). `projectObject` returns early
   * on an invisible node WITHOUT descending, and `updateMatrixWorld` skips a
   * child with `matrixWorldAutoUpdate === false`, so switching a group off
   * removes its whole subtree from both walks for one visit.
   *
   * Deliberately never the instance's ROOT group: `anim.ts` resolves targets
   * with `getObjectByName` from the root, `pickInstance` walks up from a hit to
   * find the tagged root, and `gestures.ts` writes `art.visible = true` on it
   * during a carry. Collapsing one level down keeps every one of those working
   * and still turns a prop into a single visit.
   */
  collapsed: THREE.Object3D[]
  /** Temporarily shown because the prop is animating. */
  suspended: boolean
  /**
   * `engine.now` after which a suspended instance may rejoin the batch.
   * `anim.ts` has no "clip finished" event and adding one would mean editing
   * another crew's runtime, so this is a timeout comfortably longer than the
   * longest clip. Being late costs a handful of draw calls; being early would
   * freeze a prop mid-wobble, which a kid WOULD see.
   */
  resumeAt: number
}

/**
 * How long an animating instance stays out of the batch. Comfortably longer
 * than any clip in `src/interactions/anim.ts` (the longest authored `seconds` is
 * well under a second, and a clip always ends back at its base pose). Being late
 * costs a handful of draw calls; being early would freeze a prop mid-wobble,
 * which a kid WOULD see.
 */
const SUSPEND_MS = 6000

/**
 * instanceId -> `engine.now` before which it must not be baked.
 *
 * A prop tapped in the same breath as being placed would otherwise be merged
 * MID-CLIP: `collect()` reads world matrices, so the batch would capture the
 * animated pose and then hide the real meshes, leaving the prop frozen in a
 * half-wobble forever. Baking simply waits for the clip.
 */
const animating = new Map<InstanceId, number>()

interface SceneBatch {
  sceneId: SceneId
  group: THREE.Group
  buckets: Map<string, Bucket>
  baked: Map<InstanceId, Baked>
  /** Instances that could not be batched — when there are too many, rebake. */
  skipped: Set<InstanceId>
  /**
   * Instances that must never rejoin a batch.
   *
   * `applyToggle` in `anim.ts` flips `visible` on a named part and LEAVES IT
   * flipped — that is how a lamp stays on. A merged buffer has no per-part
   * visibility, so once a kid has toggled a prop, that prop draws itself for the
   * rest of the session. It is a handful of props and a handful of draw calls,
   * and the alternative is a lamp that is on and off at the same time.
   */
  neverBatch: Set<InstanceId>
  /** `engine.now` of the last full bake, and the backlog it left behind. */
  lastFullBakeAt: number
  skippedAtLastBake: number
  /**
   * Did the last full bake decide this scene was worth batching, and how many
   * placements did it decide that against?
   *
   * The decision needs a real traversal (see `BATCH_MIN_MESHES`), which is far
   * too expensive to repeat in the twice-a-second reconcile. So the measured
   * verdict is cached here and the reconcile only re-opens it when the grid has
   * actually changed size — otherwise a scene judged not worth batching would
   * look permanently unaccounted-for and request a full bake for ever.
   */
  worth: boolean
  placementsAtLastBake: number
}

const scenes = new Map<SceneId, SceneBatch>()
let enabled = true

/** Scratch — no allocation in any repeated path. */
const _inv = new THREE.Matrix4()
const _mat = new THREE.Matrix4()
const _identity = new THREE.Matrix4()

let bakedInstances = 0
let skippedInstances = 0
let bakes = 0

/**
 * What the last full bake DECIDED, and the two numbers it decided on.
 *
 * Reported rather than merely acted on, because "the batcher did nothing" and
 * "the batcher decided doing nothing was faster" look identical from outside and
 * are opposite findings — one is a bug, one is the gate working. A tool can now
 * ask which it was.
 */
let lastVerdict: {
  sceneId: SceneId
  meshes: number
  buckets: number
  perBucket: number
  worth: boolean
} | null = null

// ---------------------------------------------------------------------------
// buckets
// ---------------------------------------------------------------------------

function bucketKey(mat: THREE.Material, cast: boolean, receive: boolean, order: number): string {
  return `${mat.uuid}|${cast ? 1 : 0}|${receive ? 1 : 0}|${order}`
}

function ensureBucket(
  sb: SceneBatch,
  key: string,
  mesh: THREE.Mesh,
  verts: number,
  indices: number,
  instances = BUCKET_INSTANCES,
): Bucket {
  let b = sb.buckets.get(key)
  if (b === undefined) {
    const mat = (Array.isArray(mesh.material) ? mesh.material[0] : mesh.material) as THREE.Material
    b = {
      key,
      material: mat,
      cast: mesh.castShadow,
      receive: mesh.receiveShadow,
      renderOrder: mesh.renderOrder,
      mesh: null,
      full: false,
    }
    sb.buckets.set(key, b)
  }
  if (b.mesh === null) {
    const v = Math.max(MIN_VERTS, Math.ceil(verts * HEADROOM))
    const i = Math.max(MIN_VERTS * 2, Math.ceil(indices * HEADROOM))
    const bm = new THREE.BatchedMesh(Math.max(8, Math.ceil(instances)), v, i, b.material)
    bm.name = `batch:${sb.sceneId}:${sb.buckets.size}`
    bm.castShadow = b.cast
    bm.receiveShadow = b.receive
    bm.renderOrder = b.renderOrder
    bm.perObjectFrustumCulled = true
    bm.sortObjects = false
    // The real meshes are still in the tree and still hit-testable; this must
    // never intercept a tap meant for one of them.
    bm.raycast = (): void => {}
    b.mesh = bm
    sb.group.add(bm)
  }
  return b
}

// ---------------------------------------------------------------------------
// baking
// ---------------------------------------------------------------------------

interface Candidate {
  key: string
  sample: THREE.Mesh
  geometries: THREE.BufferGeometry[]
  verts: number
  indices: number
  meshes: THREE.Mesh[]
}

/**
 * Collect one instance's bakeable geometry, expressed in the PLACEABLES layer's
 * space so the batch can draw it with an identity matrix (and so BatchedMesh's
 * per-object frustum culling gets a correct world-space bound for free).
 */
function collect(
  node: SceneNode,
  root: THREE.Object3D,
  live: ReadonlySet<string> | null,
): Map<string, Candidate> {
  const out = new Map<string, Candidate>()
  node.placeables.updateMatrixWorld(true)
  _inv.copy(node.placeables.matrixWorld).invert()
  root.traverse((o) => {
    const mesh = o as THREE.Mesh
    if (!bakeableMesh(mesh, root, live)) return
    const mat = (Array.isArray(mesh.material) ? mesh.material[0] : mesh.material) as THREE.Material
    const key = bucketKey(mat, mesh.castShadow, mesh.receiveShadow, mesh.renderOrder)
    const geo = mesh.geometry.clone()
    for (const attr of Object.keys(geo.attributes)) {
      if (attr !== 'position' && attr !== 'normal' && attr !== 'uv') geo.deleteAttribute(attr)
    }
    if (geo.getAttribute('normal') === undefined) geo.computeVertexNormals()
    if (geo.getAttribute('uv') === undefined) {
      const n = geo.getAttribute('position')?.count ?? 0
      geo.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(n * 2), 2))
    }
    _mat.multiplyMatrices(_inv, mesh.matrixWorld)
    geo.applyMatrix4(_mat)
    let c = out.get(key)
    if (c === undefined) {
      c = { key, sample: mesh, geometries: [], verts: 0, indices: 0, meshes: [] }
      out.set(key, c)
    }
    c.geometries.push(geo)
    c.meshes.push(mesh)
    c.verts += geo.getAttribute('position')?.count ?? 0
    c.indices += geo.getIndex()?.count ?? 0
  })
  return out
}

function mergeCandidate(c: Candidate): THREE.BufferGeometry | null {
  if (c.geometries.length === 0) return null
  if (c.geometries.length === 1) return c.geometries[0] ?? null
  try {
    const merged = mergeGeometries(c.geometries, false)
    for (const g of c.geometries) g.dispose()
    return merged
  } catch {
    for (const g of c.geometries) g.dispose()
    return null
  }
}

/** Bake ONE instance. Returns false if it had to stay unbatched. */
function bakeInstance(sb: SceneBatch, node: SceneNode, id: InstanceId): boolean {
  if (sb.neverBatch.has(id)) return false
  if ((animating.get(id) ?? 0) > engine.now) return false
  const root = node.art.get(id)
  if (root === undefined) return false
  const placement = node.grid.placement(id)
  const live = placement === null ? null : harvestTargets(placement.defId)
  const candidates = collect(node, root, live)
  if (candidates.size === 0) return false

  const entries: Entry[] = []
  const hidden: THREE.Mesh[] = []
  for (const c of candidates.values()) {
    const geo = mergeCandidate(c)
    if (geo === null) continue
    const bucket = ensureBucket(sb, c.key, c.sample, c.verts, c.indices)
    const bm = bucket.mesh
    if (bm === null || bucket.full) {
      geo.dispose()
      continue
    }
    try {
      const geometryId = bm.addGeometry(geo)
      const batchId = bm.addInstance(geometryId)
      bm.setMatrixAt(batchId, _identity)
      bm.setVisibleAt(batchId, true)
      entries.push({ bucket, geometryId, batchId })
      for (const m of c.meshes) {
        m.visible = false
        m.matrixWorldAutoUpdate = false
        hidden.push(m)
      }
    } catch {
      // Out of room in this bucket. Everything already batched for this
      // instance stays; the rest keeps drawing itself. Picture is identical.
      bucket.full = true
      geo.dispose()
    }
  }

  if (entries.length === 0) return false
  sb.baked.set(id, { entries, hidden, collapsed: collapse(root), suspended: false, resumeAt: 0 })
  bakes++
  return true
}

/** True when every mesh under `o` is already hidden by the bake. */
function fullyBaked(o: THREE.Object3D): boolean {
  let meshes = 0
  let hidden = 0
  o.traverse((c) => {
    if ((c as THREE.Mesh).isMesh !== true) return
    meshes++
    if (!c.visible) hidden++
  })
  return meshes > 0 && meshes === hidden
}

/** Switch off the highest sub-groups that the bake emptied. See `collapsed`. */
function collapse(root: THREE.Object3D): THREE.Object3D[] {
  const out: THREE.Object3D[] = []
  for (const child of root.children) {
    if (!child.visible) continue
    if (!fullyBaked(child)) continue
    child.visible = false
    child.matrixWorldAutoUpdate = false
    out.push(child)
  }
  return out
}

function uncollapse(b: Baked): void {
  for (const o of b.collapsed) {
    o.visible = true
    o.matrixWorldAutoUpdate = true
  }
  b.collapsed.length = 0
}

function unbakeInstance(sb: SceneBatch, id: InstanceId): void {
  const b = sb.baked.get(id)
  if (b === undefined) return
  for (const e of b.entries) {
    const bm = e.bucket.mesh
    if (bm === null) continue
    try {
      bm.deleteInstance(e.batchId)
      bm.deleteGeometry(e.geometryId)
    } catch {
      /* already gone — nothing to restore */
    }
  }
  uncollapse(b)
  for (const m of b.hidden) {
    m.visible = true
    m.matrixWorldAutoUpdate = true
  }
  sb.baked.delete(id)
}

/** Show an instance's own meshes for the duration of an animation. */
function suspend(sb: SceneBatch, id: InstanceId, now: number): void {
  const b = sb.baked.get(id)
  if (b === undefined) return
  b.resumeAt = now + SUSPEND_MS
  if (b.suspended) return
  b.suspended = true
  for (const e of b.entries) e.bucket.mesh?.setVisibleAt(e.batchId, false)
  for (const o of b.collapsed) {
    o.visible = true
    o.matrixWorldAutoUpdate = true
  }
  for (const m of b.hidden) {
    m.visible = true
    m.matrixWorldAutoUpdate = true
  }
}

function resume(b: Baked): void {
  if (!b.suspended) return
  b.suspended = false
  for (const e of b.entries) e.bucket.mesh?.setVisibleAt(e.batchId, true)
  for (const m of b.hidden) {
    m.visible = false
    m.matrixWorldAutoUpdate = false
  }
  for (const o of b.collapsed) {
    o.visible = false
    o.matrixWorldAutoUpdate = false
  }
}

// ---------------------------------------------------------------------------
// scene-level
// ---------------------------------------------------------------------------

function sceneBatchFor(node: SceneNode): SceneBatch {
  let sb = scenes.get(node.def.id)
  if (sb === undefined) {
    const g = new THREE.Group()
    g.name = `${node.def.id}:batch`
    node.placeables.add(g)
    sb = {
      sceneId: node.def.id,
      group: g,
      buckets: new Map(),
      baked: new Map(),
      skipped: new Set(),
      neverBatch: new Set(),
      lastFullBakeAt: -1e9,
      skippedAtLastBake: 0,
      worth: false,
      placementsAtLastBake: -1,
    }
    scenes.set(node.def.id, sb)
  }
  return sb
}

function tearDown(sb: SceneBatch): void {
  for (const id of [...sb.baked.keys()]) unbakeInstance(sb, id)
  for (const b of sb.buckets.values()) {
    if (b.mesh !== null) {
      sb.group.remove(b.mesh)
      b.mesh.dispose()
      b.mesh = null
    }
    b.full = false
  }
  sb.buckets.clear()
  sb.skipped.clear()
}

/**
 * FULL SCENE BAKE — the sizing pass. Every buffer is allocated once, to the
 * measured need plus headroom, instead of being guessed at per placement.
 */
export function bakeScene(sceneId: SceneId): void {
  if (!enabled) return
  const node = world.get(sceneId)
  if (node === undefined) return
  const sb = sceneBatchFor(node)
  tearDown(sb)
  sb.lastFullBakeAt = engine.now
  sb.skippedAtLastBake = 0
  sb.placementsAtLastBake = node.grid.count()
  sb.worth = false
  if (node.grid.count() < BATCH_MIN_PLACEMENTS) {
    bakedInstances = 0
    skippedInstances = 0
    return
  }

  // Pass 1: measure. Pass 2 reuses `bakeInstance`, which sizes each bucket from
  // the first instance it sees — so seed the buckets with the totals first.
  const totals = new Map<
    string,
    { verts: number; indices: number; instances: number; sample: THREE.Mesh }
  >()
  node.placeables.updateMatrixWorld(true)
  let bakeableMeshes = 0
  for (const p of node.grid.all()) {
    const root = node.art.get(p.instanceId)
    if (root === undefined) continue
    if (sb.neverBatch.has(p.instanceId)) continue
    const live = harvestTargets(p.defId)
    const seen = new Set<string>()
    root.traverse((o) => {
      const mesh = o as THREE.Mesh
      if (!bakeableMesh(mesh, root, live)) return
      bakeableMeshes++
      const mat = (Array.isArray(mesh.material) ? mesh.material[0] : mesh.material) as THREE.Material
      const key = bucketKey(mat, mesh.castShadow, mesh.receiveShadow, mesh.renderOrder)
      const t = totals.get(key) ?? { verts: 0, indices: 0, instances: 0, sample: mesh }
      t.verts += mesh.geometry.getAttribute('position')?.count ?? 0
      t.indices += mesh.geometry.getIndex()?.count ?? 0
      // One BatchedMesh instance per (instance, bucket) pair, not per mesh.
      if (!seen.has(key)) {
        t.instances++
        seen.add(key)
      }
      totals.set(key, t)
    })
  }

  /*
   * THE DECISION, taken on the quantity that pays for it — see
   * `BATCH_MIN_MESHES_PER_BUCKET`. Pass 1 has already counted both sides of the
   * trade, so this costs nothing extra, and bailing HERE rather than before the
   * traversal means the verdict is MEASURED on this scene's real art instead of
   * guessed from a placement count.
   */
  const perBucket = totals.size === 0 ? 0 : bakeableMeshes / totals.size
  if (bakeableMeshes < BATCH_MIN_MESHES || perBucket < BATCH_MIN_MESHES_PER_BUCKET) {
    bakedInstances = 0
    skippedInstances = 0
    lastVerdict = { sceneId, meshes: bakeableMeshes, buckets: totals.size, perBucket, worth: false }
    return
  }
  sb.worth = true
  lastVerdict = { sceneId, meshes: bakeableMeshes, buckets: totals.size, perBucket, worth: true }

  for (const [key, t] of totals) {
    ensureBucket(sb, key, t.sample, t.verts, t.indices, t.instances * HEADROOM + 8)
  }

  let baked = 0
  let skipped = 0
  for (const p of node.grid.all()) {
    if (bakeInstance(sb, node, p.instanceId)) baked++
    else {
      sb.skipped.add(p.instanceId)
      skipped++
    }
  }
  bakedInstances = baked
  skippedInstances = skipped
  sb.lastFullBakeAt = engine.now
  sb.skippedAtLastBake = skipped
}

/** Drop every batch for a scene and put its own meshes back. */
export function unbakeScene(sceneId: SceneId): void {
  const sb = scenes.get(sceneId)
  if (sb === undefined) return
  tearDown(sb)
}

// ---------------------------------------------------------------------------
// install
// ---------------------------------------------------------------------------

/** Instances waiting to be (re)baked, drained one frame after the change. */
const pending = new Map<SceneId, Set<InstanceId>>()
let pendingFullBake: SceneId | null = null

function queue(sceneId: SceneId, id: InstanceId): void {
  let s = pending.get(sceneId)
  if (s === undefined) {
    s = new Set()
    pending.set(sceneId, s)
  }
  s.add(id)
}

function drain(): void {
  if (pendingFullBake !== null) {
    const id = pendingFullBake
    pendingFullBake = null
    pending.delete(id)
    bakeScene(id)
    return
  }
  if (pending.size === 0) return
  for (const [sceneId, ids] of pending) {
    const node = world.get(sceneId)
    if (node === undefined || !node.awake) {
      pending.delete(sceneId)
      continue
    }
    const sb = sceneBatchFor(node)
    if (!sb.worth) {
      // Not worth batching as of the last measured bake. Crossing the threshold
      // is noticed by the reconcile pass below (the grid changed size, so the
      // verdict is re-taken), so nothing is left stranded.
      ids.clear()
      continue
    }
    const defer: InstanceId[] = []
    for (const id of ids) {
      if ((animating.get(id) ?? 0) > engine.now) {
        // Mid-clip. Try again once it has settled rather than freezing it.
        defer.push(id)
        continue
      }
      unbakeInstance(sb, id)
      if (node.art.has(id)) {
        if (bakeInstance(sb, node, id)) sb.skipped.delete(id)
        else sb.skipped.add(id)
      } else {
        sb.skipped.delete(id)
      }
    }
    ids.clear()
    for (const id of defer) ids.add(id)
    // Deletions leave holes and a bucket that filled up leaves props unbatched;
    // one clean rebuild costs less than living with either — but only once the
    // backlog has actually grown, and never on a hair trigger.
    if (
      sb.skipped.size > REBAKE_AFTER &&
      sb.skipped.size > sb.skippedAtLastBake + REBAKE_AFTER &&
      engine.now - sb.lastFullBakeAt > REBAKE_COOLDOWN_MS
    ) {
      pendingFullBake = sceneId
    }
  }
  for (const [sceneId, ids] of pending) {
    if (ids.size === 0) pending.delete(sceneId)
  }
}

export function installBatcher(): void {
  bus.on('scene:entered', (e) => {
    if (!enabled) return
    pendingFullBake = e.scene
  })
  // A scene we left keeps its batch: it is detached from the render graph, so it
  // costs nothing, and walking back through the door is then free.

  bus.on('build:placed', (e) => {
    if (!enabled) return
    queue(e.sceneId, e.placement.instanceId)
  })
  bus.on('build:removed', (e) => {
    if (!enabled) return
    queue(e.sceneId, e.instanceId)
  })
  bus.on('build:artStale', (e) => {
    if (!enabled) return
    for (const p of e.placements) queue(e.sceneId, p.instanceId)
  })

  // LAW #1: the tap has to move something. A batched instance cannot move, so
  // it steps out of the batch for as long as it is animating.
  bus.on('prop:anim', (e) => {
    animating.set(e.instanceId, engine.now + SUSPEND_MS)
    for (const sb of scenes.values()) suspend(sb, e.instanceId, engine.now)
  })
  // A toggle leaves a part's visibility flipped for good — see `neverBatch`.
  bus.on('prop:toggle', (e) => {
    for (const sb of scenes.values()) {
      if (!sb.baked.has(e.instanceId)) continue
      sb.neverBatch.add(e.instanceId)
      unbakeInstance(sb, e.instanceId)
      sb.skipped.add(e.instanceId)
    }
  })
}

let sinceAudit = 0

/**
 * Once a frame. Drains queued rebakes, lets finished animations rejoin, and —
 * twice a second — checks that the active scene's batch still accounts for every
 * placement in its grid.
 *
 * THAT LAST CHECK IS THE LOAD-BEARING ONE. Placements arrive by more paths than
 * the build events cover: `save.load()` restores a whole town silently, the
 * harness `placeDemo`/`clearTown`/`restoreTown` bypass the events on purpose,
 * and crews bake their own venue furniture at install time. Reconciling against
 * the grid — the one authority on what is placed — means the batch cannot drift
 * out of sync with the world no matter who put something in it.
 */
export function batcherTick(): void {
  if (!enabled) return

  const now = engine.now
  for (const sb of scenes.values()) {
    for (const b of sb.baked.values()) {
      if (b.suspended && now >= b.resumeAt) resume(b)
    }
  }
  // `animating` gains an entry per tap and would otherwise grow for the whole
  // session; a kid taps a lot of things. Expired entries mean nothing.
  if (animating.size > 0) {
    for (const [id, until] of animating) {
      if (until <= now) animating.delete(id)
    }
  }

  drain()

  sinceAudit++
  if (sinceAudit < 15) return
  sinceAudit = 0
  if (pendingFullBake !== null) return
  const activeId = world.activeSceneId
  if (activeId === null) return
  const node = world.get(activeId)
  if (node === undefined) return
  const sb = scenes.get(activeId)
  if ((pending.get(activeId)?.size ?? 0) > 0) return
  const count = node.grid.count()

  if (count < BATCH_MIN_PLACEMENTS) {
    // Below the pre-filter nothing should be batched; if something is (the kid
    // just deleted half their town), give it all back.
    if (sb !== undefined && sb.baked.size + sb.skipped.size > 0) pendingFullBake = activeId
    return
  }
  if (sb === undefined) {
    pendingFullBake = activeId
    return
  }
  /*
   * THE GRID CHANGED SIZE — re-take the verdict, whatever it was last time. This
   * is the ONLY thing that re-opens a "not worth batching" decision, and it is
   * why that decision has to be cached rather than re-derived: a scene the
   * measured bake declined would otherwise report zero accounted placements
   * against a non-zero grid and queue a full bake twice a second, for ever.
   */
  if (count !== sb.placementsAtLastBake) {
    pendingFullBake = activeId
    return
  }
  if (!sb.worth) return
  if (sb.baked.size + sb.skipped.size !== count) pendingFullBake = activeId
}

export function setBatchingEnabled(on: boolean): void {
  if (enabled === on) return
  enabled = on
  if (!on) {
    for (const id of [...scenes.keys()]) unbakeScene(id)
  } else {
    const active = world.activeSceneId
    if (active !== null) pendingFullBake = active
  }
}

export function batchingEnabled(): boolean {
  return enabled
}

/**
 * What the batcher currently believes about ONE instance. This is the invariant
 * a tap test checks: a prop a kid just touched must have its OWN meshes on
 * screen, because the merged copy cannot move.
 */
export function instanceState(id: InstanceId): {
  baked: boolean
  suspended: boolean
  neverBatch: boolean
  hiddenMeshes: number
  visibleHidden: number
} {
  for (const sb of scenes.values()) {
    const b = sb.baked.get(id)
    if (b === undefined) {
      if (sb.neverBatch.has(id) || sb.skipped.has(id)) {
        return {
          baked: false,
          suspended: false,
          neverBatch: sb.neverBatch.has(id),
          hiddenMeshes: 0,
          visibleHidden: 0,
        }
      }
      continue
    }
    return {
      baked: true,
      suspended: b.suspended,
      neverBatch: false,
      hiddenMeshes: b.hidden.length,
      visibleHidden: b.hidden.filter((m) => m.visible).length,
    }
  }
  return { baked: false, suspended: false, neverBatch: false, hiddenMeshes: 0, visibleHidden: 0 }
}

export interface BatcherReport {
  enabled: boolean
  scenes: Array<{
    sceneId: string
    buckets: number
    baked: number
    skipped: number
    suspended: number
    /** Did the last full bake judge this scene worth batching? */
    worth: boolean
  }>
  bakedInstances: number
  skippedInstances: number
  bakes: number
  /** The last full bake's decision and the two numbers behind it. */
  verdict: {
    sceneId: string
    meshes: number
    buckets: number
    perBucket: number
    worth: boolean
  } | null
}

export function batcherReport(): BatcherReport {
  return {
    enabled,
    scenes: [...scenes.values()].map((sb) => ({
      sceneId: sb.sceneId as string,
      buckets: [...sb.buckets.values()].filter((b) => b.mesh !== null).length,
      baked: sb.baked.size,
      skipped: sb.skipped.size,
      suspended: [...sb.baked.values()].filter((b) => b.suspended).length,
      worth: sb.worth,
    })),
    bakedInstances,
    skippedInstances,
    bakes,
    verdict:
      lastVerdict === null ? null : { ...lastVerdict, sceneId: lastVerdict.sceneId as string },
  }
}
